import { LogSentryException } from "@shapr3d/frontend-lib";
import { type QueryKey, useMutation, useQueryClient } from "@tanstack/react-query";
import type { IntlShape } from "react-intl";
import { z } from "zod";

import { queryKeys } from "../config";
import messages from "../locales/messages";
import { UMSrequestClient } from "../stores/requestClient";
import { accountQueryKey } from "./useAccount";
import type { AccountResponseData } from "./useAccountTypes";
import { TeamMembersQueryKey } from "./useTeams";
import type { TeamsUsersResponseData } from "./useTeamsTypes";

export type NameChangeRequestPayload = { name: string };

export type UseUpdateNameOptions = {
  mutationKey?: QueryKey;
  onSuccess?: (data: AccountResponseData, variables: NameChangeRequestPayload) => void;
  onError?: (error: unknown, variables: NameChangeRequestPayload) => void;
};

const allowedCharactersRegex = new RegExp("^[\\p{L}' \\u2019-]+$", "u");
const startsWithLetterRegex = new RegExp("^\\p{L}", "u");
const endsWithLetterRegex = new RegExp("\\p{L}$", "u");
const repeatedPunctuationRegex = /--|(?:['\u2019]{2,})/;

export const MAX_NAME_LENGTH = 70;

export const userNameSchema = z
  .string()
  .trim()
  .min(1)
  .max(MAX_NAME_LENGTH)
  .refine((value) => allowedCharactersRegex.test(value))
  .refine((value) => startsWithLetterRegex.test(value))
  .refine((value) => endsWithLetterRegex.test(value))
  .refine((value) => !repeatedPunctuationRegex.test(value));

export const createUserNameSchemaWithTranslations = (formatMessage: IntlShape["formatMessage"]) => {
  return z
    .string()
    .trim()
    .min(1, formatMessage(messages.UpdateName.Validation.MinLength))
    .max(
      MAX_NAME_LENGTH,
      formatMessage(messages.UpdateName.Validation.MaxLength, { max: MAX_NAME_LENGTH })
    )
    .refine((value) => allowedCharactersRegex.test(value), {
      message: formatMessage(messages.UpdateName.Validation.AllowedCharacters),
    })
    .refine((value) => startsWithLetterRegex.test(value), {
      message: formatMessage(messages.UpdateName.Validation.StartsWithLetter),
    })
    .refine((value) => endsWithLetterRegex.test(value), {
      message: formatMessage(messages.UpdateName.Validation.EndsWithLetter),
    })
    .refine((value) => !repeatedPunctuationRegex.test(value), {
      message: formatMessage(messages.UpdateName.Validation.RepeatedPunctuation),
    });
};

export function useUpdateName({ onSuccess, onError }: UseUpdateNameOptions = {}) {
  const queryClient = useQueryClient();

  return useMutation<AccountResponseData, Error, NameChangeRequestPayload>({
    mutationKey: [...queryKeys.authenticatedQueryKey, "request-name-change"],
    mutationFn: async ({ name }) => {
      const response = await UMSrequestClient.authenticatedRequest({
        method: "PATCH",
        url: "/account",
        body: { name },
      });

      return response.json<AccountResponseData>();
    },
    onSuccess: (data, variables) => {
      queryClient.setQueryData<AccountResponseData | undefined>(accountQueryKey, data);

      queryClient.setQueriesData<TeamsUsersResponseData | undefined>(
        { queryKey: [...TeamMembersQueryKey] },
        (oldData) => {
          if (!oldData) {
            return oldData;
          }

          const userIndex = oldData.users.findIndex((user) => user.userId === data.id);
          if (userIndex === -1) {
            return oldData;
          }

          const users = [...oldData.users];
          users.splice(userIndex, 1, {
            ...users[userIndex],
            name: variables.name,
          });

          return {
            ...oldData,
            users,
          };
        }
      );

      onSuccess?.(data, variables);
    },
    onError: (error, variables) => {
      LogSentryException({
        messagePrefix: "update-user-name",
        error,
      });
      onError?.(error, variables);
    },
  });
}
